import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Collapse,
  Snackbar
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import PlanSelector from './PlanSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import { isWireTransferEnabled } from '../utils/featureFlags';

const NextPlanPurchase = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['dashboard', 'common']);

  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    isWireTransferEnabled() ? 'wire_transfer' : 'credit_card'
  );
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: ''
  });
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [paymentValidation, setPaymentValidation] = useState({});
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [showInstantActivationConfirm, setShowInstantActivationConfirm] = useState(false);

  const checkEligibility = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/plans/next-plan-eligibility');
      setEligibility(response.data.data);
    } catch (err) {
      console.error('Error checking next plan eligibility:', err);
      setError(err.response?.data?.error || t('member.nextPlan.errors.checkEligibility'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    checkEligibility();

    // Set up periodic refresh to check for payment status updates
    const intervalId = setInterval(() => {
      checkEligibility();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId);
  }, [checkEligibility]);

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handlePurchaseNext = () => {
    setShowPlanSelector(true);
    setSelectedPlan(null);
    setPurchaseResult(null);
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowPaymentSelector(true);
    setCurrentStep(1);
    validatePlanSelection(plan);
  };

  const validatePlanSelection = (plan) => {
    const validation = { ...paymentValidation };
    validation.planSelected = !!plan;
    setPaymentValidation(validation);
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setCurrentStep(2);
    validatePaymentMethod(method);
  };

  const validatePaymentMethod = (method) => {
    const validation = { ...paymentValidation };
    validation.paymentMethodSelected = !!method;

    if (method === 'credit_card') {
      validation.cardDetailsValid = validateCardDetails();
    } else {
      validation.cardDetailsValid = true;
    }

    setPaymentValidation(validation);
  };

  const validateCardDetails = () => {
    if (selectedPaymentMethod !== 'credit_card') return true;

    const { cardNumber, cardHolder, expiryDate, cvv } = cardDetails;
    return cardNumber && cardHolder && expiryDate && cvv &&
           cardNumber.replace(/\s/g, '').length >= 13 &&
           /^(0[1-9]|1[0-2])\/\d{2}$/.test(expiryDate) &&
           /^\d{3,4}$/.test(cvv);
  };

  const handleCardDetailsChange = (details) => {
    setCardDetails(details);
    if (selectedPaymentMethod === 'credit_card') {
      const validation = { ...paymentValidation };
      validation.cardDetailsValid = validateCardDetails();
      setPaymentValidation(validation);
    }
  };

  const handleCloseDialog = () => {
    setShowPlanSelector(false);
    setShowPaymentSelector(false);
    setSelectedPlan(null);
    setSelectedPaymentMethod(isWireTransferEnabled() ? 'wire_transfer' : 'credit_card');
    setCardDetails({
      cardNumber: '',
      cardHolder: '',
      expiryDate: '',
      cvv: ''
    });
    setPurchaseResult(null);
    setError('');
    setCurrentStep(0);
    setPaymentValidation({});
    setShowPaymentInstructions(false);
  };

  const handleBackToPlans = () => {
    setShowPaymentSelector(false);
    setSelectedPlan(null);
    setCurrentStep(0);
    setPaymentValidation({});
  };

  const handleInitiatePurchase = () => {
    if (!selectedPlan) return;

    // Validate before proceeding
    if (!validatePurchaseReadiness()) {
      setError(t('member.nextPlan.errors.validationFailed'));
      return;
    }

    // Check if instant activation applies - show confirmation dialog
    const currentPlanAllowsInstantActivation = user?.membershipPlan?.allowInstantNextPlanActivation === true;

    if (currentPlanAllowsInstantActivation) {
      setShowInstantActivationConfirm(true);
    } else {
      handleProceedToPurchase();
    }
  };

  const handleProceedToPurchase = async () => {
    if (!selectedPlan) return;

    // Close confirmation dialog if open
    setShowInstantActivationConfirm(false);

    try {
      setPurchasing(true);
      setError('');

      const purchaseData = {
        planId: selectedPlan._id,
        paymentMethod: selectedPaymentMethod
      };

      // Add card details for credit card payments
      if (selectedPaymentMethod === 'credit_card') {
        purchaseData.cardDetails = cardDetails;
      }

      const response = await api.post('/plans/purchase-next', purchaseData);

      // Handle case where user already has an existing next plan or payment
      if (response.data.hasExistingNextPlan) {
        // Show existing confirmed next plan details
        setPurchaseResult({
          ...response.data.data,
          isExistingPlan: true
        });
        setCurrentStep(3);
        showNotification(response.data.message, 'info');
      } else if (response.data.hasExistingPayment) {
        // Show existing pending payment details
        setPurchaseResult({
          ...response.data.data,
          isExistingPayment: true
        });
        setCurrentStep(3);
        showNotification(response.data.message, 'warning');
      } else {
        // Handle normal purchase flow
        setPurchaseResult(response.data.data);
        setCurrentStep(3);

        // Check if instant activation occurred
        const isInstantActivation = response.data.isInstantActivation;

        // Show success notification
        const successMessage = isInstantActivation
          ? t('planSelector:instantActivation.success')
          : t('member.nextPlan.success');
        showNotification(successMessage, 'success');

        // Show payment instructions if needed
        if ((isWireTransferEnabled() && selectedPaymentMethod === 'wire_transfer') || selectedPaymentMethod === 'in_place') {
          setShowPaymentInstructions(true);
          if (isWireTransferEnabled() && selectedPaymentMethod === 'wire_transfer') {
            showNotification(t('member.nextPlan.notifications.wireTransferInstructions'), 'info');
          } else {
            showNotification(t('member.nextPlan.notifications.inPlaceInstructions'), 'info');
          }
        }
      }

      // Refresh eligibility to update UI
      await checkEligibility();

    } catch (err) {
      console.error('Error purchasing next plan:', err);
      const errorMessage = err.response?.data?.error || t('member.nextPlan.errors.purchaseFailed');
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setPurchasing(false);
    }
  };

  const validatePurchaseReadiness = () => {
    if (!selectedPlan) return false;
    if (!selectedPaymentMethod) return false;

    if (selectedPaymentMethod === 'credit_card') {
      return validateCardDetails();
    }

    return true;
  };

  const getPaymentStatusInfo = (status) => {
    const statusMap = {
      'pending': {
        icon: PendingIcon,
        color: 'warning',
        text: t('member.nextPlan.paymentStatus.pending')
      },
      'completed': {
        icon: CheckIcon,
        color: 'success',
        text: t('member.nextPlan.paymentStatus.completed')
      },
      'failed': {
        icon: ErrorIcon,
        color: 'error',
        text: t('member.nextPlan.paymentStatus.failed')
      },
      'processing': {
        icon: CircularProgress,
        color: 'info',
        text: t('member.nextPlan.paymentStatus.processing')
      }
    };

    return statusMap[status] || statusMap['pending'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>{t('member.nextPlan.checking')}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error && !eligibility) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={checkEligibility}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!eligibility) {
    return null;
  }

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('member.nextPlan.title')}
          </Typography>

          {eligibility.hasNextPlan ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('member.nextPlan.alreadyQueued')}
              </Alert>

              {/* Payment Status Display */}
              {eligibility.nextPlan.payment && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('member.nextPlan.paymentStatus.title')}
                      </Typography>
                      {(() => {
                        const statusInfo = getPaymentStatusInfo(eligibility.nextPlan.payment.status);
                        const StatusIcon = statusInfo.icon;
                        return (
                          <Chip
                            icon={<StatusIcon />}
                            label={statusInfo.text}
                            color={statusInfo.color}
                            size="small"
                          />
                        );
                      })()}
                    </Box>
                    {eligibility.nextPlan.payment.status === 'pending' && (
                      <Alert severity="info" size="small">
                        {isWireTransferEnabled() && eligibility.nextPlan.payment.method === 'wire_transfer'
                          ? t('member.nextPlan.paymentStatus.pendingWireTransfer')
                          : eligibility.nextPlan.payment.method === 'in_place'
                          ? t('member.nextPlan.paymentStatus.pendingInPlace')
                          : t('member.nextPlan.paymentStatus.pendingGeneric')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('member.nextPlan.planInfo.nextPlanStartDate')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(eligibility.nextPlan.startDate)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('member.nextPlan.planInfo.purchasedOn')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(eligibility.nextPlan.purchaseDate)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Action Button for Existing Next Plan */}
              <Button
                variant="outlined"
                color="info"
                startIcon={<InfoIcon />}
                onClick={() => checkEligibility()}
                fullWidth
                sx={{ mt: 2 }}
              >
                Refresh Plan Status
              </Button>
            </Box>
          ) : eligibility.hasPendingPayment ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  {t('dashboard:nextPlan.pending.title')}
                </Typography>
                <Typography variant="body2">
                  {t('dashboard:nextPlan.pending.message')}
                </Typography>
              </Alert>

              {/* Pending Payment Details */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('dashboard:paymentStatus.details.title')}
                    </Typography>
                    <Chip
                      icon={<PendingIcon />}
                      label={t('common:status.pending').toUpperCase()}
                      color="warning"
                      size="small"
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2">
                        <strong>{t('dashboard:paymentStatus.details.plan')}:</strong> {eligibility.pendingPayment.plan?.name || t('common:unknown')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t('common:labels.duration')}:</strong> {eligibility.pendingPayment.plan?.duration || t('common:unknown')} {t('common:days')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2">
                        <strong>{t('dashboard:paymentStatus.details.amount')}:</strong> {eligibility.pendingPayment.amount.toLocaleString()} {eligibility.pendingPayment.currency}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t('dashboard:paymentStatus.details.method')}:</strong> {t(`dashboard:paymentStatus.methods.${eligibility.pendingPayment.paymentMethod}`)}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="body2">
                        <strong>{t('dashboard:paymentStatus.details.referenceCode')}:</strong> {eligibility.pendingPayment.paymentToken}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('dashboard:paymentStatus.details.created')}: {formatDate(eligibility.pendingPayment.createdAt)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {isWireTransferEnabled() && eligibility.pendingPayment.paymentMethod === 'wire_transfer' && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {t('dashboard:nextPlan.pending.wireTransferMessage')}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Action Button for Pending Payment */}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PendingIcon />}
                onClick={() => checkEligibility()}
                fullWidth
                sx={{ mt: 2 }}
              >
                {t('dashboard:nextPlan.pending.refreshButton')}
              </Button>
            </Box>
          ) : (
            <Box>
              {eligibility.canPurchase ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {t('member.nextPlan.canPurchase')}
                    </Typography>
                    <Typography variant="body2">
                      {eligibility.reason}
                    </Typography>
                  </Alert>

                  {eligibility.currentPlan && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('member.nextPlan.planInfo.currentPlan')}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" component="div">
                        <strong>{t('member.nextPlan.planInfo.plan')}</strong> {eligibility.currentPlan.name || 'Unknown'}
                      </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="body2" component="div">
                            <strong>{t('member.nextPlan.planInfo.expires')}</strong> {formatDate(eligibility.currentPlan.endDate)}
                            {eligibility.daysUntilExpiry && (
                              <Chip
                                label={t('member.nextPlan.planInfo.daysLeft', { count: eligibility.daysUntilExpiry })}
                                size="small"
                                color={eligibility.daysUntilExpiry <= 7 ? 'warning' : 'default'}
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<PaymentIcon />}
                    onClick={handlePurchaseNext}
                    fullWidth
                  >
                    {t('member.nextPlan.purchaseButton')}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Alert 
                    severity={eligibility.reason?.includes('disabled') ? 'warning' : 'info'} 
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle2">
                      {t('member.nextPlan.notAvailable')}
                    </Typography>
                    <Typography variant="body2">
                      {eligibility.reason}
                    </Typography>
                  </Alert>

                  {eligibility.eligibleDate && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <ScheduleIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                        You can purchase your next plan starting: {formatDate(eligibility.eligibleDate)}
                      </Typography>
                    </Box>
                  )}

                  {eligibility.currentPlan && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Current plan expires: {formatDate(eligibility.currentPlan.endDate)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {eligibility.settings && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <InfoIcon sx={{ fontSize: 14, mr: 1, verticalAlign: 'middle' }} />
                    {t('member.nextPlan.eligibilityNote', { days: eligibility.settings.allowPurchaseDaysBeforeExpiry })}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Plan Selection Dialog */}
      <Dialog
        open={showPlanSelector}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('member.nextPlan.dialog.title')}
          {!purchaseResult && (
            <Box sx={{ mt: 2 }}>
              <Stepper activeStep={currentStep} alternativeLabel>
                <Step>
                  <StepLabel>{t('member.nextPlan.dialog.steps.selectPlan')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('member.nextPlan.dialog.steps.selectPayment')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('member.nextPlan.dialog.steps.confirm')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('member.nextPlan.dialog.steps.complete')}</StepLabel>
                </Step>
              </Stepper>
              {purchasing && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    {t('member.nextPlan.dialog.processing')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Show instant activation warning if current plan allows it */}
          {!purchaseResult && user?.membershipPlan?.allowInstantNextPlanActivation && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('planSelector:instantActivation.warning')}
              </Typography>
              <Typography variant="body2">
                {t('planSelector:instantActivation.description')}
              </Typography>
            </Alert>
          )}

          {purchaseResult ? (
            <Box>
              {purchaseResult.isExistingPlan ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You already have a confirmed next plan!
                  </Alert>

                  <Typography variant="h6" gutterBottom>
                    Your Confirmed Next Plan
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Plan Name
                      </Typography>
                      <Typography variant="body1">
                        {purchaseResult.existingNextPlan?.plan?.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(purchaseResult.existingNextPlan?.startDate)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1">
                        {purchaseResult.existingNextPlan?.plan?.duration} days
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Purchase Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(purchaseResult.existingNextPlan?.purchaseDate)}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              ) : purchaseResult.isExistingPayment ? (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You have a pending payment for your next plan!
                  </Alert>

                  <Typography variant="h6" gutterBottom>
                    Pending Next Plan Payment
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Plan Name
                      </Typography>
                      <Typography variant="body1">
                        {purchaseResult.existingPayment?.plan?.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Token
                      </Typography>
                      <Typography variant="body1">
                        {purchaseResult.existingPayment?.paymentToken}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body1">
                        ₺{purchaseResult.existingPayment?.amount}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Typography variant="body1">
                        {purchaseResult.existingPayment?.paymentMethod?.replace('_', ' ').toUpperCase()}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={purchaseResult.existingPayment?.status?.toUpperCase()}
                        color="warning"
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(purchaseResult.existingPayment?.createdAt)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Show wire transfer details if applicable */}
                  {purchaseResult.existingPayment?.wireTransferDetails && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Wire Transfer Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Bank Name
                          </Typography>
                          <Typography variant="body1">
                            {purchaseResult.existingPayment.wireTransferDetails.bankName}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Account Number
                          </Typography>
                          <Typography variant="body1">
                            {purchaseResult.existingPayment.wireTransferDetails.accountNumber}
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Reference Code
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary">
                            {purchaseResult.existingPayment.wireTransferDetails.referenceCode}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {t('member.nextPlan.success')}
                  </Alert>

                  <Typography variant="h6" gutterBottom>
                    {t('member.nextPlan.dialog.purchaseDetails')}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('member.nextPlan.dialog.plan')}
                      </Typography>
                      <Typography variant="body1">
                        {purchaseResult.nextPlan?.planName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('member.nextPlan.dialog.startDate')}
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(purchaseResult.nextPlan?.startDate)}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('member.nextPlan.dialog.price')}
                      </Typography>
                      <Typography variant="body1">
                        ₺{purchaseResult.nextPlan?.price}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}

              {purchaseResult.payment?.wireTransferInstructions && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="between"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowPaymentInstructions(!showPaymentInstructions)}
                  >
                    <Typography variant="h6">
                      {t('member.nextPlan.dialog.paymentInstructions')}
                    </Typography>
                    {showPaymentInstructions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>

                  <Collapse in={showPaymentInstructions}>
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info">
                        <Typography variant="body2">
                          {t('member.nextPlan.dialog.wireTransferNotice')}
                        </Typography>
                      </Alert>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('member.nextPlan.dialog.bankName')}
                        secondary={purchaseResult.payment.wireTransferInstructions.bankName}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('member.nextPlan.dialog.accountNumber')}
                        secondary={purchaseResult.payment.wireTransferInstructions.accountNumber}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('member.nextPlan.dialog.accountHolder')}
                        secondary={purchaseResult.payment.wireTransferInstructions.accountHolder}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('member.nextPlan.dialog.referenceCode')}
                        secondary={purchaseResult.payment.wireTransferInstructions.referenceCode}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('member.nextPlan.dialog.amount')}
                        secondary={`₺${purchaseResult.payment.wireTransferInstructions.amount} ${purchaseResult.payment.wireTransferInstructions.currency}`}
                      />
                    </ListItem>
                    {purchaseResult.payment.wireTransferInstructions.processingTime && (
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('member.nextPlan.dialog.processingTime')}
                          secondary={purchaseResult.payment.wireTransferInstructions.processingTime}
                        />
                      </ListItem>
                    )}
                  </List>

                      {purchaseResult.payment.wireTransferInstructions.instructions && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('member.nextPlan.dialog.additionalInstructions')}
                          </Typography>
                          <List dense>
                            {purchaseResult.payment.wireTransferInstructions.instructions.map((instruction, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={instruction} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('member.nextPlan.dialog.autoStart')}
              </Alert>

              {!showPaymentSelector ? (
                (() => {
                  const memberTypeId = user?.memberType?._id || (user?.memberType?.toString ? user?.memberType.toString() : user?.memberType);
                  console.log('User memberType:', user?.memberType);
                  console.log('MemberTypeId being passed:', memberTypeId);
                  return (
                    <PlanSelector
                      selectedPlanId={selectedPlan?._id}
                      onPlanSelect={handlePlanSelect}
                      memberTypeId={memberTypeId}
                      showMemberTypeSpecific={true}
                      currentUserPlan={user?.membershipPlan}
                    />
                  );
                })()
              ) : (
                <Box>
                  {selectedPlan && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="subtitle2">
                        Selected Plan: {selectedPlan.name}
                      </Typography>
                      <Typography variant="body2">
                        Price: ₺{selectedPlan.price} • Duration: {selectedPlan.duration} days
                      </Typography>
                    </Alert>
                  )}

                  <PaymentMethodSelector
                    selectedMethod={selectedPaymentMethod}
                    onMethodSelect={handlePaymentMethodSelect}
                    onCardDetailsChange={handleCardDetailsChange}
                    cardDetails={cardDetails}
                    amount={selectedPlan?.price || 0}
                    error={error}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!purchaseResult && showPaymentSelector && (
            <Button onClick={handleBackToPlans}>
              Back to Plans
            </Button>
          )}
          <Button onClick={handleCloseDialog}>
            {purchaseResult ? t('member.nextPlan.dialog.close') : t('member.nextPlan.dialog.cancel')}
          </Button>
          {!purchaseResult && showPaymentSelector && (
            <Button
              variant="contained"
              onClick={handleInitiatePurchase}
              disabled={!selectedPlan?._id || purchasing || !validatePurchaseReadiness()}
              color={validatePurchaseReadiness() ? "primary" : "inherit"}
            >
              {purchasing ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('member.nextPlan.dialog.processing')}
                </>
              ) : !selectedPaymentMethod ? (
                t('member.nextPlan.dialog.selectPaymentMethod')
              ) : selectedPaymentMethod === 'credit_card' && !validateCardDetails() ? (
                t('member.nextPlan.dialog.completeCardDetails')
              ) : (
                t('member.nextPlan.dialog.proceedToPurchase')
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Instant Activation Confirmation Dialog */}
      <Dialog
        open={showInstantActivationConfirm}
        onClose={() => setShowInstantActivationConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('planSelector:instantActivation.confirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('planSelector:instantActivation.confirmMessage')}
          </Alert>
          {eligibility?.currentPlan && (
            <Typography variant="body2" color="text.secondary">
              {t('planSelector:instantActivation.currentPlanWarning')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstantActivationConfirm(false)}>
            {t('planSelector:instantActivation.confirmCancel')}
          </Button>
          <Button
            onClick={handleProceedToPurchase}
            variant="contained"
            color="warning"
          >
            {t('planSelector:instantActivation.confirmProceed')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} onClose={hideNotification}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NextPlanPurchase;